import React, { useMemo } from 'react';
import cx from 'classnames';
import {
  formatCurrency,
  useLocale,
  useTranslations,
} from '@unisporkal/localization';
import { useTheme } from '@unisporkal/themes';
import type { ImagePackSavings } from 'types/isomorphic';
import PriceInfo from '../AssetSizes/PriceInfo/PriceInfo';
import styles from '../AssetSizes/AssetSizes.module.scss';
import ultraStyles from './UltraPacks.module.scss';
import useSelectedUltraPack from '../../../shared/hooks/useSelectedUltraPack';

type UltraPackListItemProps = {
  pack: ImagePackSavings;
  currencyCode: string;
};

const UltraPackListItem = ({ pack, currencyCode }: UltraPackListItemProps) => {
  const { packSize, totalAmountSaved, packPrice } = pack;
  const t = useTranslations();
  const locale = useLocale();
  const theme = useTheme();
  const themeRadio = theme.radio || {};
  const { selectedUltraPack, setSelectedUltraPack } = useSelectedUltraPack();
  const isSelected = selectedUltraPack === packSize;
  const amountYouSaveResult = useMemo(
    () =>
      formatCurrency(totalAmountSaved, locale, currencyCode, {
        minimumFractionDigits: 0,
        separateGeoTaxFromPrice: true,
      }),
    [pack.totalAmountSaved, locale, currencyCode]
  );
  const amountYouSave = amountYouSaveResult
    ? amountYouSaveResult.formattedPrice
    : '';

  const formattedPackPriceResult = useMemo(
    () =>
      formatCurrency(packPrice, locale, currencyCode, {
        minimumFractionDigits: 0,
      }),
    [pack.packPrice, locale, currencyCode]
  );
  const formattedPackPrice = formattedPackPriceResult
    ? formattedPackPriceResult.formattedPrice
    : '';

  const updateUltraPackSelection = () => {
    if (selectedUltraPack === packSize) {
      return;
    }
    setSelectedUltraPack(packSize);
  };

  return (
    <label
      className={cx(
        themeRadio.radio,
        isSelected ? styles.selectedSizeItem : styles.sizeItem
      )}
      data-testid={`${packSize}-pack`}
    >
      <div className={styles.itemDetails}>
        <input
          className={cx(themeRadio.radioButton, ultraStyles.ultraPackRadio)}
          type="radio"
          checked={isSelected}
          id={`${packSize}`}
          onChange={() => {
            updateUltraPackSelection();
          }}
          name="Ultrapack size"
        />
        <div className={styles.description}>
          <div className={ultraStyles.itemSavings}>
            {t('save_amount', { amount_saved: amountYouSave })}
          </div>
          <div className={styles.name}>
            {t('pack_count_lowercase', { total: packSize })}
          </div>
          <div className={ultraStyles.ultraPackSizeDetails}>
            <div className={styles.info}>{formattedPackPrice}</div>
          </div>
        </div>
      </div>
      <PriceInfo
        size={pack}
        currencyCode={currencyCode}
      />
    </label>
  );
};

export default UltraPackListItem;
